import type {
  BillingCompleteResponse,
  BillingUsage,
} from "@nlkb/api-contract/billing";
import { selfServeTrialPeriodDays } from "@nlkb/api-contract/billing";

import { publicListPricesUsd, teamsMonthlyUsd } from "./list-prices";
import { afterBillingSetupPath } from "./self-serve-plan";

type BillingPlan = BillingUsage["plan"];

export function queryAccountBilling(input: {
  readonly billingAccountId: string | null | undefined;
  readonly plan: BillingPlan | null | undefined;
  readonly organizationRole: "org_owner" | "org_admin" | "member" | undefined;
}): boolean {
  if (
    input.billingAccountId === null ||
    input.billingAccountId === undefined ||
    input.billingAccountId.length === 0 ||
    input.plan === null ||
    input.plan === undefined ||
    input.plan === "platform"
  ) {
    return false;
  }
  if (input.plan === "partner") return true;
  return (
    input.organizationRole === "org_owner" ||
    input.organizationRole === "org_admin"
  );
}

export const accountBillingClerkPageUrl = "workspace-billing";

export function showAccountBillingTab(
  usage: BillingUsage | undefined,
): boolean {
  if (usage === undefined) return false;
  return (
    usage.plan === "solo" || usage.plan === "teams" || usage.plan === "partner"
  );
}

export function mountAccountBillingClerkPages(input: {
  readonly queryEligible: boolean;
  readonly plan: BillingPlan | null | undefined;
  readonly usage: BillingUsage | undefined;
  readonly usageFailed: boolean;
}): boolean {
  if (!input.queryEligible || input.usageFailed) return false;
  if (input.usage !== undefined) return showAccountBillingTab(input.usage);
  return input.plan === "solo" || input.plan === "teams";
}

export function includedCreditProgressPercent(
  used: number,
  allocated: number,
): number {
  if (allocated <= 0) return used > 0 ? 100 : 0;
  return Math.min(100, Math.round((used / allocated) * 100));
}

export function selfServeTrialPeriodActive(
  plan: BillingPlan,
  periodStartIso: string,
  periodEndIso: string,
): boolean {
  if (plan !== "solo" && plan !== "teams") return false;
  const start = Date.parse(periodStartIso);
  const end = Date.parse(periodEndIso);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return false;
  }
  const trialMs = (selfServeTrialPeriodDays + 1) * 24 * 60 * 60 * 1000;
  return end - start <= trialMs;
}

export function billingPeriodLabel(
  plan: BillingPlan,
  periodStartIso: string,
  periodEndIso: string,
  status: BillingUsage["status"],
  nowMs: number = Date.now(),
): string {
  const end = formatBillingDay(periodEndIso);
  if (status === "canceled") {
    const endMs = Date.parse(periodEndIso);
    if (Number.isFinite(endMs) && endMs > nowMs) {
      return `Access until ${end}`;
    }
    return `Ended ${end}`;
  }
  if (selfServeTrialPeriodActive(plan, periodStartIso, periodEndIso)) {
    return `Trial ends ${end}`;
  }
  return `Renews ${end}`;
}

export function formatBillingDay(iso: string): string {
  const value = Date.parse(iso);
  if (!Number.isFinite(value)) return iso;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(value);
}

export function billingPlanPriceLabel(
  plan: BillingPlan,
  maxSeats: number | null,
): string | null {
  if (plan === "solo")
    return `$${String(publicListPricesUsd.soloMonthly)}/month`;
  if (plan === "teams") {
    const seats = maxSeats ?? 2;
    return `$${String(teamsMonthlyUsd(seats))}/month · ${String(seats)} seats`;
  }
  return null;
}

export function afterBillingCompletePath(
  result: BillingCompleteResponse,
): string {
  if (
    result.kind === "credits" ||
    result.kind === "resubscribe" ||
    result.plan === "partner"
  ) {
    return `/org/${result.organization_id}/knowledge`;
  }
  return afterBillingSetupPath(result.organization_id);
}

export function billingCompletePendingCopy(intent: string | null): string {
  return intent === "resubscribe"
    ? "Restoring your workspace."
    : "Creating your workspace.";
}

export function billingCompleteErrorCopy(code: string): string {
  if (code === "invalid_operation") {
    return "Checkout is not finished yet. Try again after paying, or go back and start over.";
  }
  if (code === "temporarily_unavailable") {
    return "Billing is briefly unavailable. Try again in a moment.";
  }
  return code.replaceAll("_", " ");
}

export function checkoutStartErrorCopy(
  code: string,
  development = false,
): string {
  if (code === "invalid_operation") {
    return "You already have a workspace on this account, or checkout could not start.";
  }
  if (code === "temporarily_unavailable") {
    return development
      ? "Checkout could not start. Local development uses local billing unless Stripe test mode is fully configured."
      : "Checkout could not start. Try again in a moment.";
  }
  if (code === "plan_limit") {
    return "This account cannot start another workspace on the current plan.";
  }
  return code.replaceAll("_", " ");
}

export function canStartSelfServeWorkspace(
  organizations: readonly {
    readonly role: string;
    readonly plan: string | null;
  }[],
): boolean {
  return !organizations.some(
    (organization) =>
      organization.role === "org_owner" &&
      (organization.plan === "solo" || organization.plan === "teams"),
  );
}

export function workspaceBillingBannerKind(
  usage: BillingUsage | undefined,
  organizationRole: "org_owner" | "org_admin" | "member" | undefined,
): "past_due" | null {
  if (usage === undefined) return null;
  if (usage.plan !== "solo" && usage.plan !== "teams") return null;
  if (organizationRole !== "org_owner" && organizationRole !== "org_admin") {
    return null;
  }
  if (usage.status === "past_due") return "past_due";
  return null;
}
